import { Warning, User, Clock } from "@phosphor-icons/react";
import { SUB_JOB_STATUS } from "./SubJobProgress";

const CAT_COLORS = {
  plumbing: "bg-sky-500", electrical: "bg-amber-500", painting: "bg-pink-500", carpentry: "bg-orange-600", roofing: "bg-stone-600",
  landscaping: "bg-green-600", cleaning: "bg-teal-500", hvac: "bg-indigo-500", fire_safety: "bg-red-600", security: "bg-slate-600",
  mould_eradication: "bg-lime-600", general: "bg-violet-500", other: "bg-zinc-500",
};
const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/* One day: hour ruler + one lane per sub-job; overlapping different-crew bars are flagged as clashes */
export default function DayPlanTimeline({ day, onPick }) {
  const timed = day.items.filter((i) => i.start_min != null);
  const allDay = day.items.filter((i) => i.start_min == null);
  const lo = Math.min(7 * 60, ...timed.map((i) => i.start_min));
  const hi = Math.max(18 * 60, ...timed.map((i) => i.end_min));
  const startH = Math.floor(lo / 60), endH = Math.ceil(hi / 60);
  const span = (endH - startH) * 60;
  const pct = (m) => `${((m - startH * 60) / span) * 100}%`;
  const hours = Array.from({ length: endH - startH + 1 }, (_, k) => startH + k);

  return (
    <div className="space-y-2" data-testid={`day-plan-${day.date}`}>
      {timed.length > 0 && (
        <div className="grid grid-cols-[11rem_1fr] gap-x-3">
          <div />
          <div className="relative h-4 text-[9px] text-muted-foreground">
            {hours.map((h) => <span key={h} className="absolute -translate-x-1/2" style={{ left: pct(h * 60) }}>{String(h).padStart(2, "0")}</span>)}
          </div>
          {timed.map((i) => {
            const st = SUB_JOB_STATUS[i.status] || SUB_JOB_STATUS.scheduled;
            const clash = i.clashes.length > 0;
            return (
              <div key={i.id} className="contents">
                <button type="button" onClick={() => onPick(i)} className="text-left py-1.5 min-w-0 group" data-testid={`day-plan-lane-${i.id}`}>
                  <p className="text-xs font-medium truncate group-hover:text-primary flex items-center gap-1.5">
                    <span className={`inline-block h-2 w-2 rounded-full shrink-0 ${CAT_COLORS[i.category] || CAT_COLORS.general}`} />
                    <span className="truncate">{i.sub_job_index ? `${i.sub_job_index}. ` : ""}{i.title}</span>
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5"><span className="inline-flex items-center gap-1 truncate"><User size={9} /> {i.assignee || "Unassigned"}</span><span className={`px-1.5 rounded-full text-[9px] ${st.color}`}>{st.label}</span></p>
                </button>
                <div className="relative py-1.5 border-l border-border">
                  {hours.map((h) => <span key={h} className="absolute top-0 bottom-0 border-l border-dashed border-border/60" style={{ left: pct(h * 60) }} />)}
                  <button type="button" onClick={() => onPick(i)} title={`${i.title} · ${fmt(i.start_min)}–${fmt(i.end_min)} · ${i.category.replace(/_/g, " ")}${clash ? " · clashes with another trade" : ""}`}
                    className={`relative h-8 rounded-md text-white text-[10px] font-semibold px-1.5 flex items-center justify-center gap-1 overflow-hidden shadow-sm transition-transform hover:scale-[1.01] ${CAT_COLORS[i.category] || CAT_COLORS.general} ${i.status === "completed" ? "opacity-60" : ""} ${clash ? "ring-2 ring-amber-400 ring-offset-1 ring-offset-background" : ""}`}
                    style={{ marginLeft: pct(i.start_min), width: `max(${((i.end_min - i.start_min) / span) * 100}%, 4.5rem)` }} data-testid={`day-plan-bar-${i.id}`}>
                    {clash && <Warning size={12} weight="fill" className="text-amber-200 shrink-0" data-testid={`day-plan-clash-${i.id}`} />}
                    <span className="truncate tabular-nums">{fmt(i.start_min)}–{fmt(i.end_min)}{!i.end_time ? "*" : ""}</span>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {allDay.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={`day-plan-allday-${day.date}`}>
          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1"><Clock size={10} /> No time set:</span>
          {allDay.map((i) => (
            <button key={i.id} type="button" onClick={() => onPick(i)} className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-border hover:border-primary hover:text-primary" data-testid={`day-plan-allday-${i.id}`}>
              {i.sub_job_index ? `${i.sub_job_index}. ` : ""}{i.title} · {i.assignee || "Unassigned"}
            </button>
          ))}
        </div>
      )}
      {timed.some((i) => !i.end_time) && <p className="text-[9px] text-muted-foreground">* no leave-by time set — a 2 hour visit is assumed for clash checking.</p>}
    </div>
  );
}
